var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Avatar, Box, Button, Card, CardContent, CardHeader, Divider, Grid, IconButton, Stack, Typography, } from "@mui/material";
import dayjs from "dayjs";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDeleteLeague, useRetrieveLeague, useRetrieveLeagueTeams, } from "../api/LeagueApiProvider";
import { useRetrieveUserLeaguePermissions } from "../api/PermissionApiProvider";
import { useRetrieveSeasonsByLeagueId } from "../api/SeasonApiProvider";
import { useRetrievePlayerByUserId } from "../ApiProvider";
import { FeedCard } from "../feed/FeedCard";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { NotFound } from "../nav/NotFound";
import { PlayerView } from "../players/PlayerView";
import { SeasonStatusChip } from "../seasons/SeasonStatusChip";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
import { LeagueTeamsCard } from "./LeagueTeamsCard";
var MAX_DESC = 240;
export var League = function () {
    var _a;
    var id = useNumericParams().id;
    var navigate = useNavigate();
    var _b = useRetrieveLeague({ id: id }), league = _b.data, isLoading = _b.isLoading, error = _b.error;
    var _c = useRetrievePlayerByUserId({
        userId: (league === null || league === void 0 ? void 0 : league.createdBy) ? Number(league.createdBy) : undefined,
    }), admin = _c.data, isAdminLoading = _c.isLoading, adminError = _c.error;
    var _d = __read(useState(false), 2), expanded = _d[0], setExpanded = _d[1];
    var _e = __read(useState(false), 2), showDelete = _e[0], setShowDelete = _e[1];
    var _f = __read(useState(false), 2), deleteLoading = _f[0], setDeleteLoading = _f[1];
    var _g = useRetrieveUserLeaguePermissions({
        leagueId: id,
    }), permissions = _g.data, isPermsLoading = _g.isLoading, permsError = _g.error;
    var _h = permissions || {}, _j = _h.canView, canView = _j === void 0 ? false : _j, _k = _h.canModify, canModify = _k === void 0 ? false : _k;
    var _l = useRetrieveSeasonsByLeagueId({ leagueId: id }), seasons = _l.data, isSeasonsLoading = _l.isLoading, seasonsError = _l.error;
    var _m = useRetrieveLeagueTeams({ leagueId: id, enabled: canView }), teams = _m.data, isTeamsLoading = _m.isLoading, teamsError = _m.error;
    var deleteMutation = useDeleteLeague({
        id: id,
        onMutate: function () { return setDeleteLoading(true); },
        onSuccess: function () {
            navigate("/leagues");
        },
        onError: function () {
            setDeleteLoading(false);
        },
    });
    if (!id) {
        return _jsx(NotFound, {});
    }
    if (isLoading ||
        isAdminLoading ||
        deleteLoading ||
        isSeasonsLoading ||
        isPermsLoading ||
        isTeamsLoading) {
        return _jsx(Loading, {});
    }
    if (!canView) {
        return _jsx(Forbidden, {});
    }
    if (error ||
        !league ||
        adminError ||
        seasonsError ||
        permsError ||
        teamsError) {
        return (_jsx(ErrorView, { error: error ||
                adminError ||
                seasonsError ||
                teamsError ||
                new Error("Failed to load league.") }));
    }
    var description = league.description || "";
    var isLong = description.length > MAX_DESC;
    var shownDescription = !isLong || expanded
        ? description
        : description.slice(0, MAX_DESC).trimEnd() + "…";
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: deleteMutation.isError, message: "Failed to delete league. Please try again.", onClose: function () { } }), _jsxs(Card, { sx: { mb: 3 }, children: [_jsx(CardHeader, { avatar: _jsx(Avatar, { sx: { width: 56, height: 56 }, src: league.logo || "", alt: league.name, children: !league.logo && ((_a = league.name.match(/\b(\w)/g)) === null || _a === void 0 ? void 0 : _a.join("")) }), title: _jsx(Typography, { variant: "h5", children: league.name }), subheader: league.createdAt ? (_jsxs(Typography, { variant: "caption", fontStyle: "italic", children: ["Founded \u2022 ", new Date(league.createdAt).getFullYear()] })) : undefined, action: canModify && (_jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(Link, { to: "/leagues/".concat(id, "/edit/"), children: _jsx(IconButton, { "aria-label": "edit", children: _jsx(EditIcon, {}) }) }), _jsx(IconButton, { "aria-label": "delete", onClick: function () { return setShowDelete(true); }, children: _jsx(DeleteIcon, {}) })] })) }), description && description.length > 0 && (_jsx(CardContent, { children: _jsxs(Typography, { variant: "body2", color: "text.secondary", component: "div", children: [shownDescription, isLong && (_jsx(Button, { size: "small", onClick: function () { return setExpanded(function (p) { return !p; }); }, sx: {
                                        ml: 1,
                                        textTransform: "none",
                                        padding: 0,
                                        minWidth: "auto",
                                    }, children: expanded ? "Show less" : "Read more" }))] }) }))] }), _jsx(DeleteDialog, { open: showDelete, title: "Delete ".concat(league.name, "?"), onCancel: function () { return setShowDelete(false); }, onConfirm: function () { return deleteMutation.mutate(); } }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, md: 9, order: { xs: 2, md: 1 }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: true, children: _jsx(Typography, { variant: "h6", children: "Seasons" }) }), _jsx(Grid, { item: true, children: (permissions === null || permissions === void 0 ? void 0 : permissions.canModify) && (_jsx(Box, { display: "flex", justifyContent: "flex-end", children: _jsx(Button, { variant: "contained", size: "small", component: Link, to: "/leagues/".concat(id, "/seasons/create/"), startIcon: _jsx(AddIcon, {}), children: "Create Season" }) })) }), _jsx(Grid, { item: true, xs: 12, children: seasons && seasons.length > 0 ? (_jsx(Stack, { spacing: 2, children: seasons.map(function (s) { return (_jsx(FeedCard, { icon: _jsx(CalendarMonthIcon, {}), iconColor: "#F57C00", title: s.name, subtext: s.startDate
                                                ? "Starts ".concat(dayjs(s.startDate).format("MMM D, YYYY"))
                                                : "", timestamp: s.startDate || new Date().toISOString(), to: "/seasons/".concat(s.id, "/"), action: _jsx(SeasonStatusChip, { season: s, size: "small" }) }, s.id)); }) })) : (_jsx(Typography, { variant: "body2", color: "text.secondary", children: "No seasons yet." })) })] }) }), _jsx(Grid, { item: true, xs: 12, md: 3, order: { xs: 1, md: 2 }, children: _jsxs(Stack, { spacing: 2, children: [admin && (_jsx(Card, { children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", children: "Administrator" }), _jsx(Divider, { sx: { my: 2 } }), _jsx(PlayerView, { player: admin, variant: "subtitle1" })] }) })), teams && (_jsx(LeagueTeamsCard, { teams: teams, leagueId: id, canModify: canModify }))] }) })] })] }));
};
